import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Plus, Edit, Shield, ShieldOff, Key, X, AlertCircle, Eye, EyeOff, Trash2, AlertTriangle, Database } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', role: 'bdm', department: '', status: 'active'
  });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/all`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    } finally { setLoading(false); }
  };

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({ name: '', email: '', password: '', role: 'bdm', department: '', status: 'active' });
    setError(''); setShowModal(true);
  };

  const openEditModal = (user) => {
    setModalMode('edit'); setSelectedUser(user);
    setFormData({ name: user.name, email: user.email, password: '', role: user.role, department: user.department, status: user.status });
    setError(''); setShowModal(true);
  };

  const openPasswordModal = (user) => {
    setModalMode('password'); setSelectedUser(user);
    setFormData(prev => ({ ...prev, password: '' }));
    setError(''); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    try {
      if (modalMode === 'create') {
        await axios.post(`${API}/users`, formData);
      } else if (modalMode === 'edit') {
        await axios.put(`${API}/users/${selectedUser.id}`, {
          name: formData.name, email: formData.email, role: formData.role,
          department: formData.department, status: formData.status
        });
      } else if (modalMode === 'password') {
        await axios.post(`${API}/users/${selectedUser.id}/reset-password`, { new_password: formData.password });
      }
      setShowModal(false); fetchUsers();
    } catch (err) { setError(err.response?.data?.detail || 'Operation failed'); }
  };

  const handleToggleStatus = async (user) => {
    try {
      if (user.status === 'active') {
        await axios.post(`${API}/users/${user.id}/deactivate`);
      } else {
        await axios.post(`${API}/users/${user.id}/activate`);
      }
      fetchUsers();
    } catch (error) { alert('Failed to update user status'); }
  };

  const handleDeleteUser = async (user) => {
    try {
      await axios.delete(`${API}/users/${user.id}`);
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete user'); }
  };

  const handlePurge = async (type) => {
    setPurgeLoading(true);
    try {
      const res = await axios.delete(`${API}/admin/purge/${type}`);
      alert(res.data.message);
      setPurgeConfirm('');
    } catch (err) { alert(err.response?.data?.detail || 'Purge failed'); }
    finally { setPurgeLoading(false); }
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      admin: 'bg-purple-100 text-purple-700',
      management: 'bg-blue-100 text-blue-700',
      hr: 'bg-yellow-100 text-yellow-700',
      accounts: 'bg-teal-100 text-teal-700',
      dgm: 'bg-violet-100 text-violet-700',
      senior_approver: 'bg-orange-100 text-orange-700',
      bdm: 'bg-gray-100 text-gray-700',
    };
    return classes[role] || classes.bdm;
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="text-gray-500">Loading...</div></div>;

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>User Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage users, roles, and system data</p>
        </div>
        <button onClick={openCreateModal} className="btn btn-primary" data-testid="create-user-button">
          <Plus className="w-4 h-4" /> Add User
        </button>
      </div>

      {/* Users Table */}
      <div className="card mb-6">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Department</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} data-testid={`user-row-${user.id}`}>
                  <td className="font-medium">{user.name}</td>
                  <td className="text-gray-500">{user.email}</td>
                  <td>
                    <span className={`text-xs px-2 py-1 rounded font-medium uppercase ${getRoleBadgeClass(user.role)}`}>
                      {user.role === 'bdm' ? 'BDM' : user.role === 'dgm' ? 'DGM' : user.role === 'senior_approver' ? 'Sr. Approver' : user.role}
                    </span>
                  </td>
                  <td>{user.department}</td>
                  <td>
                    <span className={`status-badge ${user.status === 'active' ? 'status-approved' : 'status-rejected'}`}>
                      {user.status}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => openEditModal(user)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded" title="Edit" data-testid={`edit-user-${user.id}`}>
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => openPasswordModal(user)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Reset password" data-testid={`reset-password-${user.id}`}>
                        <Key className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleToggleStatus(user)}
                        className={`p-1.5 rounded ${user.status === 'active' ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}`}
                        title={user.status === 'active' ? 'Deactivate' : 'Activate'} data-testid={`toggle-status-${user.id}`}>
                        {user.status === 'active' ? <ShieldOff className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => setDeleteConfirm(user)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Delete user" data-testid={`delete-user-${user.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Data Management Section */}
      <div className="card border-red-100" data-testid="data-management-section">
        <div className="flex items-center gap-2 mb-4">
          <Database className="w-4 h-4 text-red-500" />
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Data Management</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">Permanently delete all test data. This action cannot be undone.</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button onClick={() => setPurgeConfirm('expenses')}
            className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm font-medium text-red-700 hover:bg-red-100 transition-colors text-left"
            data-testid="purge-expenses-btn">
            <div className="font-semibold">Delete All Expenses</div>
            <div className="text-xs text-red-500 mt-0.5">Removes all expense records + bills</div>
          </button>
          <button onClick={() => setPurgeConfirm('trips')}
            className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm font-medium text-red-700 hover:bg-red-100 transition-colors text-left"
            data-testid="purge-trips-btn">
            <div className="font-semibold">Delete All Trips</div>
            <div className="text-xs text-red-500 mt-0.5">Removes all trip records</div>
          </button>
          <button onClick={() => setPurgeConfirm('all')}
            className="px-4 py-3 bg-red-100 border-2 border-red-300 rounded-xl text-sm font-medium text-red-800 hover:bg-red-200 transition-colors text-left"
            data-testid="purge-all-btn">
            <div className="font-semibold">Delete Everything</div>
            <div className="text-xs text-red-600 mt-0.5">All expenses + all trips + bills</div>
          </button>
        </div>
      </div>

      {/* Delete User Confirmation */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content max-w-sm p-6" onClick={e => e.stopPropagation()} data-testid="delete-user-confirm">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Delete User?</h3>
              <p className="text-sm text-gray-500 mt-1">
                Permanently delete <strong>{deleteConfirm.name}</strong> ({deleteConfirm.email})?
              </p>
              <p className="text-xs text-gray-400 mt-1">This cannot be undone.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => handleDeleteUser(deleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors text-sm"
                data-testid="confirm-delete-user-btn">
                Delete
              </button>
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 btn btn-secondary text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Purge Confirmation */}
      {purgeConfirm && (
        <div className="modal-overlay" onClick={() => setPurgeConfirm('')}>
          <div className="modal-content max-w-sm p-6" onClick={e => e.stopPropagation()} data-testid="purge-confirm-modal">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Confirm Data Purge</h3>
              <p className="text-sm text-gray-500 mt-2">
                {purgeConfirm === 'expenses' && 'This will permanently delete ALL expense records and uploaded bill files.'}
                {purgeConfirm === 'trips' && 'This will permanently delete ALL trip records.'}
                {purgeConfirm === 'all' && 'This will permanently delete ALL expenses, trips, and uploaded files.'}
              </p>
              <p className="text-xs text-red-600 font-medium mt-2">This action cannot be undone!</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => handlePurge(purgeConfirm)} disabled={purgeLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors text-sm"
                data-testid="confirm-purge-btn">
                {purgeLoading ? 'Deleting...' : 'Yes, Delete'}
              </button>
              <button onClick={() => setPurgeConfirm('')} className="flex-1 btn btn-secondary text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit/Password Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {modalMode === 'create' ? 'Add New User' : modalMode === 'edit' ? 'Edit User' : 'Reset Password'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {modalMode !== 'password' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-field" required data-testid="user-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-field" required data-testid="user-email-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                    <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="input-field" required data-testid="user-role-select">
                      <option value="bdm">BDM</option>
                      <option value="dgm">DGM</option>
                      <option value="senior_approver">Senior Approver</option>
                      <option value="hr">HR</option>
                      <option value="accounts">Accounts</option>
                      <option value="management">Management</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
                    <input type="text" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className="input-field" required data-testid="user-department-input" />
                  </div>
                  {modalMode === 'edit' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                      <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="input-field" required data-testid="user-status-select">
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  )}
                </>
              )}
              {(modalMode === 'create' || modalMode === 'password') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {modalMode === 'create' ? 'Initial Password' : 'New Password'} *
                  </label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="input-field pr-10" required minLength={8} placeholder="Min 8 chars (letters + numbers)"
                      data-testid="user-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="toggle-password-visibility">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="modal-error">
                  <p className="text-red-700 text-sm flex items-center gap-1"><AlertCircle className="w-4 h-4" />{error}</p>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="submit" className="btn btn-primary flex-1 justify-center" data-testid="submit-user-button">
                  {modalMode === 'create' ? 'Create User' : modalMode === 'edit' ? 'Update User' : 'Reset Password'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary flex-1 justify-center">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
